<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiBadgeList from '@/components/ui/UiBadgeList.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { reportDetails, reportStatus, reportType } from '@/constants/reports';
import { formatDate, titleCase } from '@/utils/format';

useSessionGuard();

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);

const headers = [
  { title: 'Student', key: 'name' },
  { title: 'Reported by', key: 'teacher_name' },
  { title: 'Subject', key: 'subject_title' },
  { title: 'Grade', key: 'grade_level' },
  { title: 'Type', key: 'report_type' },
  { title: 'Tagged', key: 'tagged', sortable: false },
  { title: 'Status', key: 'status' },
  { title: 'Date', key: 'created_at' },
];
const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/parent-records/getMyReport/${year.value}/${store.state.user.id}`);
  return (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch(year, load, { immediate: true });

const viewing = shallowRef(null);
const viewOpen = shallowRef(false);

function view(report) {
  viewing.value = report;
  viewOpen.value = true;
}
</script>

<template>
  <UiTableCard
    title="Student reports"
    description="Academic and disciplinary reports about your children."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search reports"
    empty-text="No reports found."
    row-label="name"
    :row-actions="(row) => [{ title: 'View details', icon: 'mdi-file-document-outline', onClick: () => view(row) }]"
  >
    <template #[`item.name`]="{ item }">
      <UiPerson :name="item.name" :subtitle="item.lrnNo ? `LRN ${item.lrnNo}` : ''" />
    </template>
    <template #[`item.report_type`]="{ item }">
      <UiBadge :variant="reportType(item.report_type).variant">{{ reportType(item.report_type).name }}</UiBadge>
    </template>
    <template #[`item.tagged`]="{ item }">
      <UiBadgeList :items="item.tagged || []" :max="2" />
    </template>
    <template #[`item.status`]="{ item }">
      <UiBadge :variant="reportStatus(item.status).variant">{{ reportStatus(item.status).name }}</UiBadge>
    </template>
    <template #[`item.created_at`]="{ item }">
      {{ formatDate(item.created_at) }}
    </template>
  </UiTableCard>

  <UiDialog
    v-model="viewOpen"
    :title="viewing?.name ?? 'Report'"
    :description="viewing ? `${reportType(viewing.report_type).name} concern · ${reportStatus(viewing.status).name}` : ''"
    size="lg"
    :persistent="false"
  >
    <div class="form-stack">
      <UiDetailList :items="reportDetails(viewing)" />
      <div>
        <p class="text-body-2 text-medium-emphasis mb-2">Tagged students</p>
        <UiBadgeList :items="viewing?.tagged || []" empty="No tagged students." />
      </div>
    </div>
    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
    </template>
  </UiDialog>
</template>
